import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  AlertCircle,
  Pencil,
  IdCard,
  Eye,
  Phone,
  MapPin,
  UserCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Badge } from '@/components/ui/badge';
import { SectionLabel } from '@/components/ui/section-label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { useUpdateUserProfiles } from '../../api/generated_hooks/generated-user_profiles-hooks';
import { useToast } from '@/contexts/ToastContext';
import { FormProgress } from '@/components/FormProgress';
import { cn } from '@/lib/utils';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

interface FormData {
  first_name: string;
  last_name: string;
  display_first_name: string;
  display_last_name: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  date_of_birth: string;
  gender: string;
}

type AutoCompleteValue = React.InputHTMLAttributes<HTMLInputElement>['autoComplete'];

const REQUIRED_FIELDS: (keyof FormData)[] = [
  'first_name', 'last_name', 'phone', 'address1',
  'city', 'state', 'zip', 'date_of_birth', 'gender',
];

function emptyForm(): FormData {
  return {
    first_name: '', last_name: '', display_first_name: '', display_last_name: '',
    phone: '', address1: '', address2: '', city: '', state: '', zip: '',
    date_of_birth: '', gender: '',
  };
}

function profileToForm(profile: any): FormData {
  return {
    first_name: profile?.first_name ?? '',
    last_name: profile?.last_name ?? '',
    display_first_name: profile?.display_first_name ?? '',
    display_last_name: profile?.display_last_name ?? '',
    phone: profile?.phone ?? profile?.phone_number ?? '',
    address1: profile?.address1 ?? '',
    address2: profile?.address2 ?? '',
    city: profile?.city ?? '',
    state: profile?.state ?? '',
    zip: profile?.zip ?? '',
    date_of_birth: profile?.date_of_birth ? profile.date_of_birth.substring(0, 10) : '',
    gender: profile?.gender ?? '',
  };
}

function isProfileComplete(form: FormData): boolean {
  return REQUIRED_FIELDS.every((f) => form[f]?.trim());
}

function validateAge(dob: string): string | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  if (age < 3) return 'Must be at least 3 years old';
  if (age > 100) return 'Invalid date of birth';
  return null;
}

export function ProfileInfoTab() {
  const { profile, role, user, updateProfile } = useAuthStore();
  const { showSuccess, showError } = useToast();
  const updateMutation = useUpdateUserProfiles();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<FormData>(emptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  useEffect(() => {
    if (profile) {
      const form = profileToForm(profile);
      setFormData(form);
      if (!isProfileComplete(form)) {
        setIsEditing(true);
      }
    }
  }, [profile]);

  const complete = useMemo(() => isProfileComplete(formData), [formData]);

  const roleName = role?.role_name || role?.role_type || 'user';
  const email = user?.email || (profile as any)?.primary_email || (profile as any)?.email || '';
  const displayName = formData.first_name && formData.last_name
    ? `${formData.first_name} ${formData.last_name}`
    : (profile as any)?.full_name || email || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const updateField = (field: keyof FormData, value: string) => {
    let formatted = value;
    if (field === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length > 10) return;
      formatted = formatPhone(value);
    }
    if (field === 'zip') {
      formatted = value.replace(/[^\d-]/g, '').slice(0, 10);
    }
    setFormData((prev) => ({ ...prev, [field]: formatted }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    for (const f of REQUIRED_FIELDS) {
      if (!formData[f]?.trim()) {
        errs[f] = 'Required';
      }
    }
    if (formData.gender && !['Male', 'Female'].includes(formData.gender)) {
      errs.gender = 'Select Male or Female';
    }
    const ageErr = validateAge(formData.date_of_birth);
    if (ageErr) errs.date_of_birth = ageErr;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!profile?.id) return;

    try {
      const result = await updateMutation.mutateAsync({
        id: profile.id,
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          display_first_name: formData.display_first_name || null,
          display_last_name: formData.display_last_name || null,
          phone: formData.phone,
          address1: formData.address1,
          address2: formData.address2 || null,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          date_of_birth: formData.date_of_birth,
          gender: formData.gender,
        } as any,
      });
      updateProfile({ ...profile, ...(result as any) });
      setIsEditing(false);
      showSuccess('Profile updated');
      if (returnTo) {
        window.location.href = returnTo;
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save profile');
    }
  };

  const handleCancel = () => {
    setFormData(profileToForm(profile));
    setErrors({});
    setIsEditing(false);
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 gap-4">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold shrink-0">
            {initials}
          </div>
          <div>
            <h2 className="text-lg font-semibold">{displayName}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="capitalize">
                {roleName}
              </Badge>
              {complete ? (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="h-3 w-3" /> Complete
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-amber-600">
                  <AlertCircle className="h-3 w-3" /> Incomplete
                </span>
              )}
            </div>
          </div>
        </div>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            <Pencil className="h-3 w-3 mr-1" /> Edit
          </Button>
        )}
      </CardHeader>

      <div className="px-6 pb-2">
        <FormProgress
          formData={formData as unknown as Record<string, string>}
          errors={errors as Record<string, string>}
          requiredFields={REQUIRED_FIELDS as string[]}
          optionalFields={['display_first_name', 'display_last_name']}
        />
      </div>

      <CardContent className="space-y-6">
        <FieldSection
          title="Legal Name"
          icon={<IdCard className="h-3.5 w-3.5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="First Name"
              required
              value={formData.first_name}
              error={errors.first_name}
              disabled={!isEditing}
              onChange={(v) => updateField('first_name', v)}
            />
            <FormField
              label="Last Name"
              required
              value={formData.last_name}
              error={errors.last_name}
              disabled={!isEditing}
              onChange={(v) => updateField('last_name', v)}
            />
          </div>
        </FieldSection>

        <FieldSection
          title="Display Name"
          description="Optional — shown publicly instead of legal name"
          icon={<Eye className="h-3.5 w-3.5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Display First Name"
              value={formData.display_first_name}
              disabled={!isEditing}
              onChange={(v) => updateField('display_first_name', v)}
            />
            <FormField
              label="Display Last Name"
              value={formData.display_last_name}
              disabled={!isEditing}
              onChange={(v) => updateField('display_last_name', v)}
            />
          </div>
        </FieldSection>

        <FieldSection
          title="Contact Information"
          icon={<Phone className="h-3.5 w-3.5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Phone"
              required
              value={formData.phone}
              error={errors.phone}
              disabled={!isEditing}
              onChange={(v) => updateField('phone', v)}
              type="tel"
            />
            <FormField
              label="Email"
              value={email}
              disabled
            />
          </div>
        </FieldSection>

        <FieldSection
          title="Address"
          icon={<MapPin className="h-3.5 w-3.5" />}
        >
          <div className="space-y-4">
            <FormField
              label="Address Line 1"
              required
              value={formData.address1}
              error={errors.address1}
              disabled={!isEditing}
              onChange={(v) => updateField('address1', v)}
            />
            <FormField
              label="Address Line 2"
              value={formData.address2}
              disabled={!isEditing}
              onChange={(v) => updateField('address2', v)}
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <FormField
                  label="City"
                  required
                  value={formData.city}
                  error={errors.city}
                  disabled={!isEditing}
                  onChange={(v) => updateField('city', v)}
                />
              </div>
              <div>
                <Label className="text-sm">
                  State <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.state}
                  onValueChange={(v) => updateField('state', v)}
                  disabled={!isEditing}
                >
                  <SelectTrigger className={errors.state ? 'border-destructive' : ''}>
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.state && (
                  <p className="text-xs text-destructive mt-1">{errors.state}</p>
                )}
              </div>
              <div>
                <FormField
                  label="ZIP"
                  required
                  value={formData.zip}
                  error={errors.zip}
                  disabled={!isEditing}
                  onChange={(v) => updateField('zip', v)}
                />
              </div>
            </div>
          </div>
        </FieldSection>

        <FieldSection
          title="Personal Information"
          icon={<UserCircle className="h-3.5 w-3.5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">
                Date of Birth <span className="text-destructive">*</span>
              </Label>
              <DatePicker
                value={formData.date_of_birth}
                onChange={(val) => updateField('date_of_birth', val)}
                disabled={!isEditing}
                placeholder="Select date of birth"
                className={errors.date_of_birth ? 'border-destructive' : ''}
              />
              {errors.date_of_birth && (
                <p className="text-xs text-destructive mt-1">{errors.date_of_birth}</p>
              )}
            </div>
            <div>
              <Label className="text-sm">
                Gender <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.gender}
                onValueChange={(v) => updateField('gender', v)}
                disabled={!isEditing}
              >
                <SelectTrigger className={errors.gender ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
              {errors.gender && (
                <p className="text-xs text-destructive mt-1">{errors.gender}</p>
              )}
            </div>
          </div>
        </FieldSection>

        {isEditing && (
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FieldSection({
  title,
  description,
  icon,
  children,
}: {
  title: string;
  description?: string;
  /** Optional leading icon — gives each form section a clearer identity. */
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <SectionLabel icon={icon} color="muted">
        {title}
      </SectionLabel>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {children}
    </div>
  );
}

function FormField({
  label,
  value,
  error,
  required,
  disabled,
  type = 'text',
  autoComplete,
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  type?: string;
  autoComplete?: AutoCompleteValue;
  onChange?: (value: string) => void;
}) {
  // When read-only, render the value as static text instead of an <input>.
  // A disabled native input invites browser autofill to paint a ghost suggestion
  // on top of the saved value (visible as overlapping characters in the ZIP field
  // after save). Static text removes that surface entirely.
  const readOnlyDisplay = disabled;

  return (
    <div>
      <Label className="text-sm">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {readOnlyDisplay ? (
        <div
          className={cn(
            'flex h-10 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm text-muted-foreground',
            error && 'border-destructive'
          )}
        >
          {value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </div>
      ) : (
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          autoComplete={autoComplete}
          className={error ? 'border-destructive' : ''}
        />
      )}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
